import { discoverySourceLabels } from "./discovery-sources";
import type { CarrierPurchasingOption } from "@/api/carriers";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  useGetCarrierServices,
  useDeleteCarrierService,
  useUpdateCarrierService,
  type CarrierServiceListItem,
  type DeleteCarrierServiceConflictDetails,
} from "@/api/carrier-services";
import { Plus, RefreshCw, Ruler, Trash2 } from "lucide-react";
import axios from "axios";
import { AddCarrierServiceDialog } from "./add-carrier-service-dialog";
import { DiscoverServicesDialog } from "./discover-services-dialog";
import { EditCarrierServiceLimitsDialog } from "./edit-carrier-service-limits-dialog";

interface CarrierServicesSectionProps {
  carrierId: string;
  carrierName: string;
  carrierCode: string;
  channels?: CarrierPurchasingOption[];
}

export function CarrierServicesSection({
  carrierId,
  carrierName,
  carrierCode,
  channels = [],
}: CarrierServicesSectionProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [estimateEdit, setEstimateEdit] =
    useState<CarrierServiceListItem | null>(null);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [pendingDelete, setPendingDelete] =
    useState<CarrierServiceListItem | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    service: CarrierServiceListItem;
    details: DeleteCarrierServiceConflictDetails;
  } | null>(null);
  const [limitsEdit, setLimitsEdit] = useState<CarrierServiceListItem | null>(
    null
  );

  const servicesQuery = useGetCarrierServices({
    carrierId,
    active: undefined,
  });

  const updateMutation = useUpdateCarrierService({
    onSuccess: (data) => {
      toast.success(data.isActive ? "Service enabled" : "Service disabled", {
        description: data.isActive
          ? `${data.code} is now available for new rate bands.`
          : `${data.code} is no longer available for new rate bands.`,
      });
    },
    onError: (err) => {
      toast.error("Failed to update service", { description: err.message });
    },
  });

  const deleteMutation = useDeleteCarrierService({
    onSuccess: (data) => {
      toast.success("Service disabled", {
        description: `${data.code} is no longer available for new rate bands.`,
      });
      setPendingDelete(null);
      setConfirmDelete(null);
    },
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const data = err.response.data as
          | {
              error?: string;
              details?: DeleteCarrierServiceConflictDetails;
            }
          | undefined;
        if (data?.error === "DELETE_REQUIRES_CONFIRM" && data.details) {
          if (pendingDelete) {
            setConfirmDelete({
              service: pendingDelete,
              details: data.details,
            });
            setPendingDelete(null);
          }
          return;
        }
      }
      toast.error("Failed to disable service", { description: err.message });
      setPendingDelete(null);
    },
  });

  const handleDelete = (service: CarrierServiceListItem) => {
    setPendingDelete(service);
    deleteMutation.mutate({ id: service.id });
  };

  const handleConfirmDelete = () => {
    if (!confirmDelete) return;
    setPendingDelete(confirmDelete.service);
    deleteMutation.mutate({ id: confirmDelete.service.id, confirm: true });
  };

  const services = servicesQuery.data ?? [];
  const sourceLabels = discoverySourceLabels(channels);
  const canDiscover = channels.some((channel) => {
    const provider = channel.apiProvider;
    return (
      channel.active &&
      provider?.active === true &&
      (provider.code === "shipstation" ||
        (provider.code === "chitchats" &&
          carrierCode.toLowerCase() === "chitchats"))
    );
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium">Services</div>
          <p className="text-xs text-muted-foreground">
            Shipping services available to configure in the pricing workspace.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add manually
          </Button>
          {canDiscover && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDiscoverOpen(true)}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Discover from API
            </Button>
          )}
        </div>
      </div>

      {!canDiscover && (
        <p className="text-sm text-muted-foreground">
          Add services manually, or enable a supported label provider under
          Shipping prices &amp; labels to use discovery.
        </p>
      )}
      <div className="overflow-x-auto rounded-md border">
        <Table className="min-w-[680px]">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">ETA</TableHead>
              <TableHead className="text-right">DIM cm</TableHead>
              <TableHead>Sources</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {servicesQuery.isLoading && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground py-4"
                >
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!servicesQuery.isLoading && services.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground py-6"
                >
                  No services yet.{" "}
                  {canDiscover
                    ? "Click 'Discover from API' to populate from the configured providers."
                    : "Click 'Add manually' to create the first service."}
                </TableCell>
              </TableRow>
            )}
            {services.map((s) => (
              <TableRow key={s.id} className={!s.isActive ? "opacity-50" : ""}>
                <TableCell className="font-mono">{s.code}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Edit delivery estimate for ${s.name}`}
                    onClick={() => setEstimateEdit(s)}
                  >
                    {s.estimatedDays ?? "Set days"}
                  </Button>
                </TableCell>
                <TableCell className="text-right">
                  {s.dimFactorCm != null ? Number(s.dimFactorCm) : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(s.sourceProviderIds ?? []).slice(0, 3).map((id) => (
                      <Badge
                        key={id}
                        variant="secondary"
                        className="text-xs font-mono"
                      >
                        {sourceLabels.get(id) ??
                          "Unavailable purchasing option"}
                      </Badge>
                    ))}
                    {(s.sourceProviderIds ?? []).length === 0 && (
                      <span className="text-xs text-muted-foreground">
                        manual
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={s.isActive}
                      onCheckedChange={(checked) =>
                        updateMutation.mutate({
                          id: s.id,
                          input: { isActive: checked },
                        })
                      }
                      disabled={updateMutation.isPending}
                      aria-label={
                        s.isActive ? "Disable service" : "Enable service"
                      }
                    />
                    <span className="text-xs text-muted-foreground">
                      {s.isActive ? "active" : "disabled"}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setLimitsEdit(s)}
                      title="Package limits"
                    >
                      <Ruler className="h-3 w-3" />
                    </Button>
                    {s.isActive && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => handleDelete(s)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AddCarrierServiceDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        carrierId={carrierId}
        carrierName={carrierName}
      />

      {discoverOpen && (
        <DiscoverServicesDialog
          open={discoverOpen}
          onClose={() => setDiscoverOpen(false)}
          carrierId={carrierId}
          carrierName={carrierName}
          carrierCode={carrierCode}
          channels={channels}
        />
      )}

      {limitsEdit && (
        <EditCarrierServiceLimitsDialog
          open
          onClose={() => setLimitsEdit(null)}
          service={limitsEdit}
        />
      )}

      {estimateEdit && (
        <DeliveryEstimateDialog
          service={estimateEdit}
          onClose={() => setEstimateEdit(null)}
        />
      )}

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disable {confirmDelete?.service.code}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.details.message} Historical data is preserved;
              this only hides the service from new rate bands.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
            >
              Disable anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DeliveryEstimateDialog({
  service,
  onClose,
}: {
  service: CarrierServiceListItem;
  onClose: () => void;
}) {
  const [value, setValue] = useState(service.estimatedDays?.toString() ?? "");
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateCarrierService({
    onSuccess: () => {
      toast.success("Delivery estimate saved");
      onClose();
    },
    onError: (cause) =>
      setError(cause.message || "Could not save the estimate"),
  });
  const close = () => {
    if (!update.isPending) onClose();
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delivery estimate — {service.name}</DialogTitle>
          <DialogDescription>
            Enter business days. Use 0 for same-day delivery. Service-area
            coverage is configured separately.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (update.isPending) return;
            const days = Number(value);
            if (
              value.trim() === "" ||
              !Number.isInteger(days) ||
              days < 0 ||
              days > 2147483647
            ) {
              setError("Enter a valid whole number of days (0 or more).");
              return;
            }
            setError(null);
            update.mutate({ id: service.id, input: { estimatedDays: days } });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="service-estimated-days">
              Estimated delivery days
            </Label>
            <Input
              id="service-estimated-days"
              type="number"
              min={0}
              step={1}
              value={value}
              disabled={update.isPending}
              onChange={(event) => {
                setValue(event.target.value);
                setError(null);
              }}
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={update.isPending}
              onClick={close}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save estimate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
